import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

export function highlightSearchText(
  text: string,
  searchTerms: string[],
  keyPrefix = "match",
): ReactNode[] {
  const terms = normalizeSearchTerms(searchTerms);
  if (terms.length === 0) {
    return [text];
  }

  // Build one case-insensitive global matcher so any search term can be
  // highlighted in a single pass through the text.
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi");
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) != null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    nodes.push(
      <mark
        key={`${keyPrefix}-${match.index}`}
        className="rounded bg-yellow-300 px-0.5 text-slate-950"
      >
        {match[0]}
      </mark>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

export function highlightSearchTermsInNodes(
  nodes: ReactNode[],
  searchTerms: string[],
) {
  if (searchTerms.length === 0) {
    return nodes;
  }

  return nodes.flatMap((node, index) =>
    highlightSearchTermsInNode(node, searchTerms, `highlight-${index}`),
  );
}

function highlightSearchTermsInNode(
  node: ReactNode,
  searchTerms: string[],
  keyPrefix: string,
): ReactNode | ReactNode[] {
  if (typeof node === "string") {
    return highlightSearchText(node, searchTerms, keyPrefix);
  }

  if (isValidElement(node)) {
    const element = node as ReactElement<{ children?: ReactNode }>;
    const children = element.props.children;

    if (children == null) {
      return element;
    }

    // YAML syntax highlighting already wraps tokens in spans. Clone those
    // wrappers so search highlights can be inserted without losing token styles.
    return cloneElement(
      element,
      undefined,
      highlightSearchTermsInReactNode(
        children,
        searchTerms,
        `${keyPrefix}-child`,
      ),
    );
  }

  return node;
}

function highlightSearchTermsInReactNode(
  node: ReactNode,
  searchTerms: string[],
  keyPrefix: string,
): ReactNode[] {
  return Children.toArray(node).flatMap((child, index) =>
    highlightSearchTermsInNode(child, searchTerms, `${keyPrefix}-${index}`),
  );
}

function normalizeSearchTerms(searchTerms: string[]) {
  return Array.from(
    new Set(searchTerms.map((term) => term.trim()).filter(Boolean)),
    // Match longer terms first so "cache.prefer" wins over "cache".
  ).sort((a, b) => b.length - a.length);
}

function escapeRegExp(value: string) {
  // Search terms should be matched literally, so characters like "." and "*"
  // need escaping before they are interpolated into the highlight RegExp.
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
